<template>
  <v-dialog v-model="showDialog" width="700" persistent>
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <span>{{ dialogTitle }}</span>
        <v-btn icon variant="text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert v-if="submitError" type="error" density="compact" class="mb-3" closable @click:close="submitError = ''">
          {{ submitError }}
        </v-alert>
        <v-form ref="formRef" v-model="formValid" @submit.prevent="submit">
          <v-text-field
            v-model="serverName"
            label="Title / Server Name"
            :rules="serverNameRules"
            :counter="120"
            required
            :readonly="mode === 'external_removal'"
            variant="outlined"
            density="compact"
            class="mb-2"
          />

          <v-textarea
            v-model="reason"
            label="Reason"
            :rules="reasonRules"
            :counter="500"
            required
            variant="outlined"
            density="compact"
            rows="3"
            class="mb-2"
          />

          <div class="mb-2">
            <div class="text-subtitle-2 mb-1">Links</div>
            <div v-for="(link, index) in links" :key="index" class="d-flex align-center mb-1">
              <v-text-field
                v-model="links[index]"
                :label="`Link ${index + 1}`"
                :rules="linkRules"
                variant="outlined"
                density="compact"
                hide-details="auto"
                class="flex-grow-1"
              />
              <v-btn icon variant="text" size="small" @click="removeLink(index)" class="ml-1">
                <v-icon size="small">mdi-close</v-icon>
              </v-btn>
            </div>
            <v-btn
              v-if="links.length < 10"
              variant="text"
              size="small"
              color="primary"
              prepend-icon="mdi-plus"
              @click="addLink"
            >
              Add Link
            </v-btn>
          </div>

          <v-textarea
            v-model="notes"
            label="Notes"
            :rules="notesRules"
            :counter="1000"
            variant="outlined"
            density="compact"
            rows="2"
            class="mb-2"
          />
        </v-form>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!formValid || submitting"
          :loading="submitting"
          @click="submit"
        >
          Submit Request
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import type { RequestMcpServerInstallPayload } from '@/types/mcp'
import { isApiError } from '@/types/api'

@Options({
  props: {
    show: Boolean,
    mode: {
      type: String,
      default: 'platform_install'
    },
    initialServerName: {
      type: String,
      default: ''
    }
  },
  watch: {
    show: {
      handler(value: boolean) {
        if (value === true) {
          ;(this as any).resetForm()
        }
      },
      immediate: true
    }
  },
  emits: ['close', 'submitted']
})
export default class McpServerRequestOverlay extends Vue {
  show!: boolean
  mode!: 'platform_install' | 'external_removal'
  initialServerName!: string

  serverName = ''
  reason = ''
  links: string[] = []
  notes = ''
  formValid = false
  submitting = false
  submitError = ''

  get showDialog(): boolean {
    return this.show
  }

  get dialogTitle(): string {
    return this.mode === 'external_removal' ? 'Request Shared Server Removal' : 'Request MCP Server'
  }

  serverNameRules = [
    (v: string) => !!v?.trim() || 'Server name is required',
    (v: string) => (v?.trim().length ?? 0) <= 120 || 'Must be 120 characters or less'
  ]

  reasonRules = [
    (v: string) => !!v?.trim() || 'Reason is required',
    (v: string) => (v?.trim().length ?? 0) <= 500 || 'Must be 500 characters or less'
  ]

  linkRules = [
    (v: string) => {
      if (!v || !v.trim()) return true
      if (v.trim().length > 2048) return 'Must be 2048 characters or less'
      try {
        const url = new URL(v.trim())
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return 'Must be an http or https URL'
      } catch {
        return 'Must be a valid URL'
      }
      return true
    }
  ]

  notesRules = [
    (v: string) => !v || v.trim().length <= 1000 || 'Must be 1000 characters or less'
  ]

  addLink() {
    if (this.links.length < 10) {
      this.links.push('')
    }
  }

  removeLink(index: number) {
    this.links.splice(index, 1)
  }

  close() {
    this.resetForm()
    this.$emit('close')
  }

  resetForm() {
    this.serverName = this.mode === 'external_removal' ? this.initialServerName : ''
    this.reason = ''
    this.links = []
    this.notes = ''
    this.submitting = false
    this.formValid = false
    this.submitError = ''
  }

  async submit() {
    if (!this.formValid || this.submitting) return

    this.submitting = true
    this.submitError = ''

    const payload: RequestMcpServerInstallPayload = {
      requestType: this.mode,
      serverName: this.serverName.trim(),
      reason: this.reason.trim(),
      links: this.links.map(l => l.trim()).filter(l => l.length > 0),
      notes: this.notes.trim() || undefined
    }

    const response = await this.$store.dispatch('requestMcpServerInstall', payload)

    this.submitting = false

    if (isApiError(response)) {
      this.submitError = response.message || 'Failed to submit request. Please try again.'
      return
    }

    if (!response.success) {
      this.submitError = response.error || 'Failed to submit request. Please try again.'
      return
    }

    this.resetForm()
    this.$emit('submitted')
  }

  mounted() {
    this.resetForm()
  }
}
</script>
